import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  InputSearch,
  TableData,
  Pagination,
  DateRangeFilter,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import {
  Column,
  Repossession,
  RepossessionStatus,
  RepossessionSeverity,
  Model,
  Oem,
} from '@/types/common';
import {
  getListRepossessions,
  createRepossession,
  updateRepossession,
  markRepossessionAsFoundNew,
  markRepossessionAsLost,
  CreateRepossessionRequest,
  UpdateRepossessionRequest,
} from '@/services/api/repossessions';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import { UniversalFilter } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '@/utils';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import RepoModal, { type RepoFormData } from './components/RepoModal';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface RepoFilters {
  statuses?: RepossessionStatus[];
  severities?: RepossessionSeverity[];

  oemIDs?: string[];
  modelIDs?: string[];
}

interface RepoRequestParams {
  page: string;
  size: string;
  search?: string;
  statuses?: string;
  severities?: string;
  modelIDs?: string;
  oemIDs?: string;
  startDate?: string;
  endDate?: string;
}

const RepoListPage: React.FC = () => {
  const navigate = useNavigate();
  const forceReposApproval = useFeatureFlag(
    'web_fms_force_repos_approval',
    'boolean'
  );

  const getStatusDisplay = (status: RepossessionStatus) => {
    switch (status) {
      case RepossessionStatus.OPEN:
        return { label: 'Open', className: 'bg-blue-100 text-blue-800' };
      case RepossessionStatus.IN_PROGRESS:
        return {
          label: 'In Progress',
          className: 'bg-yellow-100 text-yellow-800',
        };
      case RepossessionStatus.FOUND:
        return { label: 'Found', className: 'bg-green-100 text-green-800' };
      case RepossessionStatus.PENDING_APPROVAL:
        return {
          label: 'Pending Approval',
          className: 'bg-purple-100 text-purple-800',
        };
      case RepossessionStatus.POTENTIAL_LOSS:
        return {
          label: 'Potensi Kehilangan',
          className: 'bg-red-100 text-red-800',
        };
      case RepossessionStatus.WRITTEN_OFF:
        return {
          label: 'Written Off',
          className: 'bg-[#FFF3EB] text-[#FA7319]',
        };
      case RepossessionStatus.CLOSED:
        return { label: 'Closed', className: 'bg-gray-100 text-gray-800' };
      default:
        return { label: status, className: 'bg-gray-100 text-gray-800' };
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [repoData, setRepoData] = useState<Repossession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState<RepoFilters>({});
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null,
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingRepo, setEditingRepo] = useState<Repossession | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  );
  const [pageSize, setPageSize] = useState(10);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [oems, setOems] = useState<Oem[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Models',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: Model) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'severities',
        title: 'Severity',
        icon: 'ri-alert-line',
        multiple: true,
        dataSource: [
          { id: RepossessionSeverity.LOW, name: 'Low' },
          { id: RepossessionSeverity.MEDIUM, name: 'Medium' },
          { id: RepossessionSeverity.HIGH, name: 'High' },
          { id: RepossessionSeverity.CRITICAL, name: 'Critical' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
      {
        key: 'statuses',
        title: 'Status',
        icon: 'ri-flag-line',
        multiple: true,
        dataSource: [
          { id: RepossessionStatus.OPEN, name: 'Open' },
          { id: RepossessionStatus.IN_PROGRESS, name: 'In Progress' },
          { id: RepossessionStatus.FOUND, name: 'Found' },
          { id: RepossessionStatus.PENDING_APPROVAL, name: 'Pending Approval' },
          {
            id: RepossessionStatus.POTENTIAL_LOSS,
            name: 'Potensi Kehilangan',
          },
          { id: RepossessionStatus.WRITTEN_OFF, name: 'Written Off' },
          { id: RepossessionStatus.CLOSED, name: 'Closed' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
    ],
    [models, oems]
  );

  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const oemsResponse = await getListOem({
          page: '1',
          size: '9999',
        });
        setOems(oemsResponse.data || []);

        const modelsResponse = await getListModel({
          page: '1',
          size: '9999',
        });
        setModels(modelsResponse.data || []);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load filter data'
        );
        showErrorToast('Error', errorMessage);
      }
    };

    loadFilterData();
  }, []);

  const fetchRepossessions = useCallback(
    async (
      page: number = 1,
      search?: string,
      filters?: RepoFilters,
      size?: number,
      range?: DateRange
    ) => {
      setIsLoading(true);
      try {
        const params: RepoRequestParams = {
          page: page.toString(),
          size: size?.toString() || '10',
        };

        if (search?.trim()) {
          params.search = search.trim();
        }

        if (filters?.statuses && filters.statuses.length > 0) {
          params.statuses = filters.statuses.join(',');
        }

        if (filters?.severities && filters.severities.length > 0) {
          params.severities = filters.severities.join(',');
        }

        if (filters?.oemIDs && filters.oemIDs.length > 0) {
          params.oemIDs = filters.oemIDs.join(',');
        }

        if (filters?.modelIDs && filters.modelIDs.length > 0) {
          params.modelIDs = filters.modelIDs.join(',');
        }

        const dateRangeToUse = range;
        if (dateRangeToUse?.from) {
          const year = dateRangeToUse.from.getFullYear();
          const month = String(dateRangeToUse.from.getMonth() + 1).padStart(
            2,
            '0'
          );
          const day = String(dateRangeToUse.from.getDate()).padStart(2, '0');
          params.startDate = `${year}-${month}-${day}`;
        }
        if (dateRangeToUse?.to) {
          const year = dateRangeToUse.to.getFullYear();
          const month = String(dateRangeToUse.to.getMonth() + 1).padStart(
            2,
            '0'
          );
          const day = String(dateRangeToUse.to.getDate()).padStart(2, '0');
          params.endDate = `${year}-${month}-${day}`;
        }

        const response = await getListRepossessions(params);

        setRepoData(response.data || []);
        setTotal(response.pagination?.total || 0);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load repossessions'
        );
        showErrorToast('Error', errorMessage);
        setRepoData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setCurrentPage(1);
  }, []);

  const handleFiltersApply = useCallback((filters: RepoFilters) => {
    setActiveFilters(filters);
    setCurrentPage(1);
  }, []);

  const handleFiltersClear = useCallback(() => {
    setActiveFilters({});
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    fetchRepossessions(
      currentPage,
      searchTerm,
      activeFilters,
      pageSize,
      dateRange
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, activeFilters, pageSize, dateRange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [actionDropdownOpen]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleSort = (field: string, direction: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const sortedRepoData = useMemo(() => {
    if (!sortField || !sortDirection) {
      return repoData;
    }

    return [...repoData].sort((a: Repossession, b: Repossession) => {
      const aValue = a[sortField as keyof Repossession];
      const bValue = b[sortField as keyof Repossession];

      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;
      if (aValue === bValue) return 0;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [repoData, sortField, sortDirection]);

  const handleEditRepo = useCallback((repo: Repossession) => {
    setEditingRepo(repo);
    setActionDropdownOpen(null);
  }, []);

  const handleMarkAsFound = useCallback(
    async (repo: Repossession) => {
      if (!repo.ID) return;

      try {
        const userEmail = getUserEmail();

        // Call the mark-as-found API endpoint using the service
        await markRepossessionAsFoundNew(repo.ID, {
          recoveryCost: 0,
          recoveryNotes: 'Marked as found from list',
          updatedBy: userEmail,
        });

        showSuccessToast('Success', 'Repossession marked as found');
        setActionDropdownOpen(null);

        // Refresh the data
        fetchRepossessions(
          currentPage,
          searchTerm,
          activeFilters,
          pageSize,
          dateRange
        );
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to mark repossession as found'
        );
        showErrorToast('Error', errorMessage);
      }
    },
    [
      currentPage,
      searchTerm,
      activeFilters,
      pageSize,
      dateRange,
      fetchRepossessions,
    ]
  );

  const handleMarkAsLost = useCallback(
    async (repo: Repossession) => {
      if (!repo.ID) return;

      try {
        const userEmail = getUserEmail();

        // Call the mark-as-lost API endpoint using the service
        await markRepossessionAsLost(repo.ID, {
          updatedBy: userEmail,
        });

        showSuccessToast('Success', 'Repossession marked as lost');
        setActionDropdownOpen(null);

        // Refresh the data
        fetchRepossessions(
          currentPage,
          searchTerm,
          activeFilters,
          pageSize,
          dateRange
        );
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to mark repossession as lost'
        );
        showErrorToast('Error', errorMessage);
      }
    },
    [
      currentPage,
      searchTerm,
      activeFilters,
      pageSize,
      dateRange,
      fetchRepossessions,
    ]
  );

  const createRepoActions = useCallback(
    (repo: Repossession): ActionDropdownAction[] => {
      const actions: ActionDropdownAction[] = [
        {
          key: 'detail',
          label: 'Detail',
          icon: 'ri-eye-line',
          onClick: () => {
            navigate(`/repossessions/${repo.ID}`);
          },
          separateAfter: true,
        },
      ];
      if (
        repo.status === RepossessionStatus.OPEN ||
        repo.status === RepossessionStatus.IN_PROGRESS ||
        repo.status === RepossessionStatus.POTENTIAL_LOSS
      ) {
        actions.push({
          key: 'edit',
          label: 'Edit',
          icon: 'ri-edit-line',
          onClick: () => handleEditRepo(repo),
          separateAfter: true,
        });
      }

      if (
        forceReposApproval &&
        (repo.status === RepossessionStatus.OPEN ||
          repo.status === RepossessionStatus.POTENTIAL_LOSS)
      ) {
        actions.push({
          key: 'found',
          label: 'Mark as Found',
          icon: 'ri-check-line',
          onClick: () => handleMarkAsFound(repo),
        });

        actions.push({
          key: 'lost',
          label: 'Mark as Lost',
          icon: 'ri-close-line',
          onClick: () => handleMarkAsLost(repo),
        });
      }

      return actions;
    },
    [
      forceReposApproval,
      handleEditRepo,
      handleMarkAsFound,
      handleMarkAsLost,
      navigate,
    ]
  );

  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const handleCreateRepo = async (data: RepoFormData) => {
    setIsModalLoading(true);
    try {
      const userEmail = getUserEmail();
      const createData: CreateRepossessionRequest = {
        handoverID: data.handoverID || '',
        reason: data.reason,
        notes: data.notes,
        assignedOpsTeam: data.assignedOpsTeam,
        createdBy: userEmail,
        severity: data.severity,
      };

      await createRepossession(createData);
      showSuccessToast('Success', 'Repossession created successfully');
      setIsAddModalOpen(false);
      fetchRepossessions(
        currentPage,
        searchTerm,
        activeFilters,
        pageSize,
        dateRange
      );
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to create repossession'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleUpdateRepo = async (data: RepoFormData) => {
    if (!editingRepo?.ID) return;

    setIsModalLoading(true);
    try {
      const userEmail = getUserEmail();
      const updateData: UpdateRepossessionRequest = {
        handoverID: data.handoverID,
        reason: data.reason,
        notes: data.notes,
        assignedOpsTeam: data.assignedOpsTeam,
        severity: data.severity,
        updatedBy: userEmail,
      };

      await updateRepossession(editingRepo?.ID, updateData);
      showSuccessToast('Success', 'Repossession case updated successfully');
      setEditingRepo(null);
      fetchRepossessions(
        currentPage,
        searchTerm,
        activeFilters,
        pageSize,
        dateRange
      );
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to update repossession case'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsModalLoading(false);
    }
  };

  const columns: Column[] = [
    {
      key: 'code',
      title: 'RepoID',
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'platNomor',
      title: 'Vehicle',
      sortable: true,
      render: (value: string, record: Repossession) => (
        <div className="text-[14px]">
          <div className="font-medium">{value || '-'}</div>
          <div className="text-gray-500 text-[11px]">
            {record.oemName + ' - ' + record.modelName || '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'driverName',
      title: 'Mitra',
      sortable: true,
      render: (value: string, record: Repossession) => (
        <div
          className={`text-[14px] ${record.driverID
            ? 'text-underline text-blue-600 cursor-pointer hover:text-blue-800'
            : 'text-gray-900 cursor-default'
            }`}
          onClick={(e) => {
            if (record.driverID) {
              e.stopPropagation();
              const backOfficeUrl =
                process.env.REACT_APP_BACK_OFFICE_URL ||
                'https://back-office.dashelectric.co';
              window.open(
                `${backOfficeUrl}/driver/${record.driverID}`,
                '_blank'
              );
            }
          }}
        >
          <div>{value || '-'}</div>
          <div className="text-gray-500 text-[11px]">
            {record.driverCode || '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'clientName',
      title: 'Client',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'hubName',
      title: 'Pitstop',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      align: 'center',
      sortable: true,
      render: (value: RepossessionStatus) => {
        if (!value)
          return (
            <div className="text-center">
              <span className="text-[14px]">-</span>
            </div>
          );

        const statusInfo = getStatusDisplay(value);
        return (
          <div className="text-center">
            <span
              className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${statusInfo.className}`}
            >
              {statusInfo.label}
            </span>
          </div>
        );
      },
    },
    {
      key: 'severity',
      title: 'Severity',
      align: 'center',
      sortable: true,
      render: (value: RepossessionSeverity) => (
        <div className="text-center">
          {value ? (
            <span
              className={`inline-flex items-center gap-2 rounded-lg border px-2 py-1 text-xs font-medium text-gray-700 border-gray-200`}
            >
              <span
                className={`h-2 w-2 rounded-full ${value === RepossessionSeverity.LOW
                    ? 'bg-green-500'
                    : value === RepossessionSeverity.MEDIUM
                      ? 'bg-blue-500'
                      : value === RepossessionSeverity.HIGH
                        ? 'bg-orange-500'
                        : value === RepossessionSeverity.CRITICAL
                          ? 'bg-red-500'
                          : 'bg-gray-400'
                  }`}
              />
              {value}
            </span>
          ) : (
            <span className="text-[14px]">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">
          {value ? new Date(value).toLocaleDateString('en-GB') : '-'}
        </span>
      ),
    },
    {
      key: 'updatedAt',
      title: 'Last Updated',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">
          {value ? new Date(value).toLocaleDateString('en-GB') : '-'}
        </span>
      ),
    },
    {
      key: 'assignedOpsTeam',
      title: 'Assigned to',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'createdBy',
      title: 'Created By',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-20',
      align: 'center',
      render: (_value: unknown, record: Repossession, index?: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={createRepoActions(record)}
            ariaLabel={`Actions for repossession ${record.ID}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Repossession>({
    storageKey: 'repossessions',
    columns,
  });

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Repossessions
            </h1>
            <p className="text-[14px] text-gray-600">
              Track, report, and close repossession cases.
            </p>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => setIsAddModalOpen(true)}
          >
            <i className="ri-add-line text-[12px] mr-1"></i>
            Create Repo
          </Button>
        </div>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              onDateRangeChange={handleDateRangeChange}
              initialRange={dateRange}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={handleFiltersApply}
              onClear={handleFiltersClear}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">
                Loading repossessions...
              </span>
            </div>
          ) : (
            <TableData
              data={sortedRepoData}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              onRowClick={(repo) => navigate(`/repossessions/${repo.ID}`)}
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1);
        }}
      />

      <RepoModal
        isOpen={isAddModalOpen && !editingRepo}
        onClose={() => {
          setIsAddModalOpen(false);
        }}
        onSubmit={handleCreateRepo}
        isLoading={isModalLoading}
        mode="create"
      />

      <RepoModal
        isOpen={!!editingRepo}
        onClose={() => {
          setEditingRepo(null);
        }}
        onSubmit={handleUpdateRepo}
        isLoading={isModalLoading}
        mode="edit"
        repoId={editingRepo?.ID}
      />
    </div>
  );
};

export default RepoListPage;
